import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import DeletionCaseStatsGrid from "../../../shared/components/DeletionCaseStatsGrid";
import DeletionCasesFilterBar from "../../../client/dashboard/components/DeletionCasesFilterBar";
import { useTranslation } from "../../../i18n";
import { mergeSavedItemOutcomes } from "../../../shared/utils/deletionCaseOutcomes";
import AdminDeletionCasesTable from "../components/AdminDeletionCasesTable";
import {
  fetchAdminDeletionCases,
  fetchAdminDeletionCaseStats,
  formatAdminError,
  updateAdminDeletionCase,
} from "../adminApi";

const PAGE_SIZE = 25;

const AdminDeletionCasesPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [cases, setCases] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [statusGroupFilter, setStatusGroupFilter] = useState("");
  const [createdWithinDaysFilter, setCreatedWithinDaysFilter] = useState(null);
  const [violationFilter, setViolationFilter] = useState("");
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  useEffect(() => {
    const nav = location.state;
    if (!nav) return;
    if (nav.statusGroup) {
      setStatusGroupFilter(nav.statusGroup);
      setStatusFilter("");
      setCreatedWithinDaysFilter(null);
    } else if (nav.status) {
      setStatusFilter(nav.status);
      setStatusGroupFilter("");
      setCreatedWithinDaysFilter(null);
    } else if (nav.createdWithinDays) {
      setCreatedWithinDaysFilter(nav.createdWithinDays);
      setStatusFilter("");
      setStatusGroupFilter("");
    }
    setPage(1);
  }, [location.state]);

  const hasActiveFilters = Boolean(
    statusFilter ||
      statusGroupFilter ||
      createdWithinDaysFilter ||
      violationFilter ||
      search.trim()
  );

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const data = await fetchAdminDeletionCaseStats();
      setStats(data);
    } catch {
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadCases = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchAdminDeletionCases({
        page,
        pageSize: PAGE_SIZE,
        status: statusFilter || undefined,
        statusGroup: statusGroupFilter || undefined,
        createdWithinDays: createdWithinDaysFilter || undefined,
        violationType: violationFilter || undefined,
        search: search.trim() || undefined,
        ordering: "-created_at",
      });
      setCases(data.results);
      setTotalCount(data.count);
    } catch (err) {
      setError(formatAdminError(err));
      setCases([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    statusFilter,
    statusGroupFilter,
    createdWithinDaysFilter,
    violationFilter,
    search,
  ]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadCases();
    }, search ? 350 : 0);
    return () => window.clearTimeout(timer);
  }, [loadCases, search]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, statusGroupFilter, createdWithinDaysFilter, violationFilter]);

  const patchCaseInList = (updated) => {
    setCases((prev) =>
      prev.map((c) =>
        c.id === updated.id
          ? {
              ...c,
              ...updated,
              items: updated.items ?? c.items,
              outcomeSummary: updated.outcomeSummary ?? c.outcomeSummary,
            }
          : c
      )
    );
  };

  const handleStatusChange = async (caseId, newStatus) => {
    const current = cases.find((c) => c.id === caseId);
    if (
      newStatus === "completed" &&
      current?.outcomeSummary?.pending > 0
    ) {
      setError(t("adminCases.outcomeHint"));
      return;
    }
    setUpdatingId(caseId);
    setError("");
    try {
      const updated = await updateAdminDeletionCase(caseId, { status: newStatus });
      patchCaseInList(updated);
      loadStats();
    } catch (err) {
      setError(formatAdminError(err));
    } finally {
      setUpdatingId(null);
    }
  };

  const handleSaveOutcomes = async (caseId, itemOutcomes) => {
    setUpdatingId(caseId);
    setError("");
    try {
      const updated = await updateAdminDeletionCase(caseId, {
        itemOutcomes: itemOutcomes.map((row) => ({
          id: row.id,
          deletionOutcome: row.deletion_outcome,
        })),
      });
      patchCaseInList(mergeSavedItemOutcomes(updated, itemOutcomes));
    } catch (err) {
      setError(formatAdminError(err));
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="bg-background font-body-md text-on-surface">
      <div className="mx-auto w-full max-w-none px-margin-mobile py-stack-lg md:px-margin-desktop">
        <header className="mb-stack-lg">
          <h1 className="font-headline-lg text-headline-lg text-primary">
            {t("adminCases.title")}
          </h1>
        </header>

        <DeletionCaseStatsGrid
          stats={stats}
          loading={statsLoading}
          activeStatus={statusFilter}
          activeStatusGroup={statusGroupFilter}
          activeCreatedWithinDays={createdWithinDaysFilter}
          onFilterChange={({ status, statusGroup, createdWithinDays }) => {
            setStatusFilter(status || "");
            setStatusGroupFilter(statusGroup || "");
            setCreatedWithinDaysFilter(createdWithinDays ?? null);
            setPage(1);
          }}
        />

        <DeletionCasesFilterBar
          statusFilter={statusFilter}
          statusGroupFilter={statusGroupFilter}
          onStatusFilterChange={(value) => {
            setStatusFilter(value);
            if (value) {
              setStatusGroupFilter("");
              setCreatedWithinDaysFilter(null);
            }
            setPage(1);
          }}
          violationFilter={violationFilter}
          onViolationFilterChange={(v) => {
            setViolationFilter(v);
            setPage(1);
          }}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={t("adminCases.searchPlaceholder")}
        />

        {error ? (
          <p className="mb-4 rounded-xl border border-error/30 bg-error-container px-4 py-2 text-body-md text-on-error-container">
            {error}
          </p>
        ) : null}

        {loading ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-outline-variant/30 bg-surface-container-lowest py-16">
            <div
              className="h-10 w-10 animate-spin rounded-full border-4 border-outline-variant border-t-secondary"
              aria-hidden
            />
            <p className="mt-4 text-body-md text-on-surface-variant">{t("adminCases.loading")}</p>
          </div>
        ) : cases.length === 0 ? (
          <div className="tonal-card rounded-2xl border border-outline-variant/30 px-6 py-14 text-center">
            <p className="font-headline-md text-primary">
              {hasActiveFilters
                ? t("adminCases.noMatches")
                : t("cases.noCasesYet")}
            </p>
            <p className="mt-2 text-body-md text-on-surface-variant">
              {hasActiveFilters
                ? t("cases.noCasesFilterHint")
                : t("cases.noCasesYetHint")}
            </p>
          </div>
        ) : (
          <>
            <p className="mb-3 text-label-sm text-on-surface-variant">
              {t("adminCases.totalRequests", { count: totalCount })}
            </p>
            <AdminDeletionCasesTable
              cases={cases}
              expandedId={expandedId}
              onToggleExpand={(id) =>
                setExpandedId((prev) => (prev === id ? null : id))
              }
              onStatusChange={handleStatusChange}
              onSaveOutcomes={handleSaveOutcomes}
              updatingId={updatingId}
            />
            {totalPages > 1 ? (
              <div className="mt-6 flex items-center justify-center gap-4">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded-lg border border-outline-variant px-4 py-2 text-label-sm font-medium text-on-surface-variant transition hover:bg-surface-container-low disabled:opacity-40"
                >
                  {t("common.back")}
                </button>
                <span className="text-label-sm text-on-surface-variant">
                  {t("admin.pageOf", { page, total: totalPages })}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded-lg border border-outline-variant px-4 py-2 text-label-sm font-medium text-on-surface-variant transition hover:bg-surface-container-low disabled:opacity-40"
                >
                  {t("auth.next")}
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDeletionCasesPage;
